
import React from "react";

// ========================================
// PRODUCT SALES INFORMATION
// ========================================

const ProductSalesInformation = ({
    data = {},
    onChange
}) => {

    // ========================================
    // DATA
    // ========================================

    const salesInformation = {
        minimumOrderQuantity: "",
        quotationRequirements: [],
        leadQualificationCriteria: [],
        salesNotes: "",
        ...(data || {})
    };

    // ========================================
    // STYLES
    // ========================================

    const inputClass =
        "w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-300";

    const labelClass =
        "block text-sm font-medium text-gray-700 mb-2";

    const addButtonClass =
        "px-4 py-2 bg-gray-800 text-white text-sm rounded hover:bg-gray-700";

    const removeButtonClass =
        "px-3 py-2 bg-red-50 text-red-700 border border-red-200 text-sm rounded hover:bg-red-100";

    // ========================================
    // UPDATE FIELD
    // ========================================

    const updateField = (field, value) => {

        onChange({
            ...salesInformation,
            [field]: value
        });

    };

    // ========================================
    // ARRAY MANAGEMENT
    // ========================================

    const getItems = (field) => {

        return Array.isArray(salesInformation[field])
            ? salesInformation[field]
            : [];

    };

    const addItem = (field) => {

        updateField(
            field,
            [
                ...getItems(field),
                ""
            ]
        );

    };

    const updateItem = (
        field,
        index,
        value
    ) => {

        updateField(
            field,
            getItems(field).map(
                (item, i) =>
                    i === index
                        ? value
                        : item
            )
        );

    };

    const removeItem = (
        field,
        index
    ) => {

        updateField(
            field,
            getItems(field).filter(
                (_, i) => i !== index
            )
        );

    };

    // ========================================
    // REUSABLE LIST SECTION
    // ========================================

    const renderListSection = (
        field,
        title,
        description,
        placeholder,
        addLabel
    ) => {

        const items = getItems(field);

        return (

            <section className="space-y-5">

                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 border-b pb-4">

                    <div>

                        <h3 className="text-base font-semibold text-gray-900">
                            {title}
                        </h3>

                        <p className="text-sm text-gray-500 mt-1">
                            {description}
                        </p>

                    </div>

                    <button
                        type="button"
                        onClick={() => addItem(field)}
                        className={addButtonClass}
                    >
                        + {addLabel}
                    </button>

                </div>

                {items.length === 0 && (

                    <div className="py-8 text-center border border-dashed rounded-lg">

                        <p className="text-sm text-gray-500">
                            No entries added yet.
                        </p>

                    </div>

                )}

                {items.map((item, index) => (

                    <div
                        key={index}
                        className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                    >

                        <div className="flex flex-col sm:flex-row gap-3">

                            <div className="flex-1 min-w-0">

                                <label className={labelClass}>
                                    {title} — {index + 1}
                                </label>

                                <textarea
                                    value={item || ""}
                                    onChange={(event) =>
                                        updateItem(
                                            field,
                                            index,
                                            event.target.value
                                        )
                                    }
                                    placeholder={placeholder}
                                    rows={3}
                                    className={inputClass}
                                />

                            </div>

                            <div className="sm:self-end">

                                <button
                                    type="button"
                                    onClick={() =>
                                        removeItem(field, index)
                                    }
                                    className={removeButtonClass}
                                >
                                    Remove
                                </button>

                            </div>

                        </div>

                    </div>

                ))}

            </section>

        );

    };

    // ========================================
    // RENDER
    // ========================================

    return (

        <div className="space-y-10">

            {/* MINIMUM ORDER QUANTITY */}

            <section className="space-y-4">

                <h3 className="text-base font-semibold text-gray-900 border-b pb-3">
                    Minimum Order Quantity
                </h3>

                <p className="text-sm text-gray-500">
                    Specify the minimum quantity or commercial
                    ordering conditions applicable to this product.
                </p>

                <div>

                    <label className={labelClass}>
                        Minimum Order Quantity
                    </label>

                    <input
                        type="text"
                        value={
                            salesInformation.minimumOrderQuantity || ""
                        }
                        onChange={(event) =>
                            updateField(
                                "minimumOrderQuantity",
                                event.target.value
                            )
                        }
                        placeholder="e.g. 10 bags, 100 kg, or subject to quotation"
                        className={inputClass}
                    />

                    <p className="text-xs text-gray-500 mt-2">
                        This is a text field so you can specify
                        quantities, packaging units or special
                        commercial conditions.
                    </p>

                </div>

            </section>

            {/* QUOTATION REQUIREMENTS */}

            {renderListSection(
                "quotationRequirements",
                "Quotation Requirements",
                "Define the information required before the sales team can prepare an accurate quotation.",
                "e.g. Estimated application area in square metres.",
                "Requirement"
            )}

            {/* LEAD QUALIFICATION CRITERIA */}

            {renderListSection(
                "leadQualificationCriteria",
                "Lead Qualification Criteria",
                "Define the information or conditions used to determine whether an enquiry is sufficiently qualified for the sales team.",
                "e.g. Customer has provided the project location and approximate material requirement.",
                "Criterion"
            )}

            {/* SALES NOTES */}

            <section className="space-y-4">

                <h3 className="text-base font-semibold text-gray-900 border-b pb-3">
                    Internal Sales Notes
                </h3>

                <p className="text-sm text-gray-500">
                    Record additional commercial guidance for
                    the sales team, including pricing policies,
                    enquiry handling instructions or escalation
                    requirements.
                </p>

                <div>

                    <label className={labelClass}>
                        Sales Notes
                    </label>

                    <textarea
                        value={
                            salesInformation.salesNotes || ""
                        }
                        onChange={(event) =>
                            updateField(
                                "salesNotes",
                                event.target.value
                            )
                        }
                        placeholder="Enter internal sales guidance, commercial considerations and special instructions."
                        rows={6}
                        className={inputClass}
                    />

                </div>

                <p className="text-xs text-gray-500">
                    These notes may contain internal business
                    information and should not be disclosed
                    directly to customers by the AI agent.
                </p>

            </section>

            {/* GUIDANCE */}

            <div className="border-t pt-4">

                <p className="text-xs text-gray-500">
                    Sales information helps the AI agent collect
                    relevant enquiry details and identify when
                    a customer is ready for a quotation.
                    The agent should not invent prices,
                    discounts, availability or commercial terms.
                </p>

            </div>

        </div>

    );

};

export default ProductSalesInformation;
